import React, { useState } from "react";
import { Plus, X, Settings2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { SEVERITY_META, categoryIcon } from "@/lib/meta";
import { RUNBOOK } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import CategoryManagerDialog from "@/components/CategoryManagerDialog";

export default function Sidebar({ meta, filters, setFilter, clearFilters, reload, open, onClose }) {
  const { isAdmin, isContributor, startGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [manageOpen, setManageOpen] = useState(false);
  const categories = meta?.categories || [];
  const catCounts = meta?.category_counts || {};
  const sevCounts = meta?.severity_counts || {};
  const environments = meta?.environments || [];

  const handleCreate = () => (isContributor ? navigate("/new") : startGoogleLogin("/new"));

  // Sidebar filters belong to the Runbook Library. If the user is viewing
  // a runbook detail/edit page, return to the library when a filter is chosen.
  const handleFilter = (key, value) => {
    // Store filters in the URL so the library always reflects the selected
    // category/severity/environment, even when coming from a runbook detail page.
    const params = new URLSearchParams(location.search);
    if (value) params.set(key, value);
    else params.delete(key);
    navigate({ pathname: "/", search: params.toString() ? `?${params.toString()}` : "" });
    onClose?.();
  };

  const content = (
    <div className="flex flex-col gap-7 h-full">
      <Button
        data-testid={RUNBOOK.createBtn}
        onClick={handleCreate}
        className="w-full gap-2 justify-center"
      >
        <Plus className="h-4 w-4" /> New Runbook
      </Button>

      <Section
        title="Categories"
        action={
          isAdmin && (
            <button
              type="button"
              data-testid="manage-categories-btn"
              onClick={() => setManageOpen(true)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
              aria-label="Manage categories"
            >
              <Settings2 className="h-3.5 w-3.5" /> Manage
            </button>
          )
        }
      >
        <FilterRow
          active={!filters.category}
          onClick={() => handleFilter("category", "")}
          label="All categories"
          count={meta?.total}
          testId={RUNBOOK.categoryFilter("all")}
        />
        {categories.map((c) => {
          const Icon = categoryIcon(c);
          return (
            <FilterRow
              key={c}
              active={filters.category === c}
              onClick={() => handleFilter("category", filters.category === c ? "" : c)}
              label={c}
              icon={<Icon className="h-4 w-4" />}
              count={catCounts[c] || 0}
              testId={RUNBOOK.categoryFilter(c)}
            />
          );
        })}
      </Section>

      <Section title="Severity">
        {Object.entries(SEVERITY_META).map(([key, m]) => (
          <FilterRow
            key={key}
            active={filters.severity === key}
            onClick={() => handleFilter("severity", filters.severity === key ? "" : key)}
            label={m.label}
            icon={<span className={cn("h-2.5 w-2.5 rounded-full", m.dot)} />}
            count={sevCounts[key] || 0}
            testId={RUNBOOK.severityFilter(key)}
          />
        ))}
      </Section>

      <Section title="Environment">
        {environments.map((e) => (
          <FilterRow
            key={e}
            active={filters.environment === e}
            onClick={() => handleFilter("environment", filters.environment === e ? "" : e)}
            label={e}
            testId={RUNBOOK.envFilter(e)}
          />
        ))}
      </Section>

      {(filters.category || filters.severity || filters.environment || filters.tag) && (
        <Button variant="ghost" size="sm" onClick={() => { clearFilters(); if (location.pathname !== "/") navigate("/"); onClose?.(); }} className="justify-start gap-2 text-muted-foreground">
          <X className="h-4 w-4" /> Clear all filters
        </Button>
      )}
    </div>
  );

  return (
    <>
      {/* desktop */}
      <aside className="hidden md:block w-64 flex-shrink-0 border-r border-border p-5 overflow-y-auto tr-scroll">
        {content}
      </aside>
      {/* mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={onClose} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-background border-r border-border p-5 overflow-y-auto tr-scroll tr-fade-up">
            <div className="flex justify-end mb-2">
              <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
                <X className="h-5 w-5" />
              </Button>
            </div>
            {content}
          </aside>
        </div>
      )}
      {isAdmin && (
        <CategoryManagerDialog
          open={manageOpen}
          onOpenChange={setManageOpen}
          categories={categories}
          counts={catCounts}
          onChanged={() => {
            if (filters.category) setFilter("category", "");
            reload();
          }}
        />
      )}
    </>
  );
}

function Section({ title, action, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between px-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h4>
        {action}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function FilterRow({ active, onClick, label, icon, count, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
        active ? "bg-primary/10 text-primary font-medium" : "text-foreground/80 hover:bg-accent"
      )}
    >
      {icon}
      <span className="flex-1 text-left truncate">{label}</span>
      {count != null && <span className="text-xs text-muted-foreground tabular-nums">{count}</span>}
    </button>
  );
}
